import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useRunReportQuery,
  useGetReportSpecificFieldsQuery,
} from "../../../api/network/reportApiServices";
import { TModalsState } from "../../../api/store/commonSlice";
import svgIcons from "../../../assets/svg/svgIcons";
import AdminTable from "../../../components/adminTable";
import { APP_CONFIG } from "../../../constants/constants";
import { jsTimeToDBTime } from "../../../utils/dateTime";
import ChartWrapper from "../components/chartWrapper";
import ModalSwitchChartType from "../components/modalSwitchChartType";
import { IWidgetProps } from "../components/overviewDragDropColumnItem";
import PillSelector from "../components/pillSelector";
import WidgetChartAreaGenerator, {
  widgetBarColorLightBlue,
} from "../components/widgetChartAreaGenerator";
import WidgetFilterSelector from "../components/widgetFilterSelector";
import { useOverviewContext } from "../context/overview.context";
import ModalWidgetDetails from "../modalWidgetDetails";
import { EWidgetChartTypes } from "../types/overview.types";
import { verticalLabelFormatter } from "../utils/chart";
import { useGroupsAndAssetsUtils } from "../hooks/overviewScreenUtils";

const tableMinHeightClass = "min-h-[100px]";
const tableMaxHeightClass = "max-h-[224px]";

const detailsTablePriorityColumns = ["501", "502"];

const defaultChartType = EWidgetChartTypes.VERTICAL_BAR_GRAPH;

interface IWidgetSafetyEventsAsset extends IWidgetProps {
  showDetails: boolean;
  setShowDetails: (value: boolean) => void;
}

const WidgetSafetyEventsAsset: FC<IWidgetSafetyEventsAsset> = ({
  widgetType,
  showDetails,
  setShowDetails,
}) => {
  const { t: tOverview } = useTranslation("translation", {
    keyPrefix: "overviewScreen",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: `overviewScreen.widgets.enum_${widgetType}`,
  });

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const [selectedChartType, setSelectedChartType] = useState(defaultChartType);

  const pills = ["Score", "Count"];
  const [selectedPill, setSelectedPill] = React.useState(pills[0]);

  const reportId = selectedPill === "Score" ? 84 : 107;

  const {
    allTimeFilters,
    timeFilter,
    allGroupsSelected,
    selectedGroups,
    selectedDataType,
    setSelectedDataType,
    allAssetsSelected,
    selectedAssets,
    setAllAssetsSelected,
    setSelectedAssets,
  } = useOverviewContext();

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const {
    data: dataSpecificReport,
    isFetching: isFetchingSpecificReport,
    isUninitialized: isUninitializedSpecificReport,
  } = useGetReportSpecificFieldsQuery(
    {
      // type: REPORT_TYPES.CUSTOM,
      report_id: reportId,
    },
    { refetchOnMountOrArgChange: true }
  );

  const graphXAxisDataKey = selectedPill === "Score" ? "501" : "994";
  const graphYAxisDataKey = selectedPill === "Score" ? "978" : "948";
  const ordering = `${sortOrder === "desc" ? "" : "-"}${graphYAxisDataKey}`;
  const averageValueFieldId = selectedPill === "Score" ? "13951" : "13952";
  // const { data: dataReport, isFetching } = useRunReportQuery(
  //   {
  //     id: reportId.toString(),
  //     // fields: '443,444,447'.split(','),
  //     ordering: `${sortOrder === "asc" ? "-" : ""}${graphYAxisDataKey}`,

  //     page: 1,
  //     page_size: 10,

  //     // filters
  //     device_groups:
  //       allGroupsSelected === false && !!selectedGroups
  //         ? selectedGroups
  //         : undefined,
  //     start_time: !!allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.timeRange[0]
  //       ? jsTimeToDBTime(
  //           allTimeFilters
  //             .find((filter) => filter.slug === timeFilter)
  //             ?.timeRange[0]?.getTime() ?? 0
  //         )
  //       : undefined,
  //     end_time: !!allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.timeRange[1]
  //       ? jsTimeToDBTime(
  //           allTimeFilters
  //             .find((filter) => filter.slug === timeFilter)
  //             ?.timeRange[1]?.getTime() ?? 0
  //         )
  //       : undefined,
  //     ds_time_range: allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.value
  //   },
  //   {
  //     skip: isUninitializedSpecificReport || isFetchingSpecificReport,
  //     refetchOnMountOrArgChange: true
  //   }
  // );

  const {
    reportData: dataReport,
    isFetching,
    isError,
    reportError,
  } = useGroupsAndAssetsUtils(
    selectedDataType,
    reportId,
    selectedAssets,
    1,
    10,
    ordering
  );

  const MemoizedWidgetFilterSelector = // useCallback(
    ({ limitWidth = true }: { limitWidth?: boolean }) => {
      return (
        <div className="mb-4 flex flex-row-reverse Xgroup-[]:flex-row justify-between">
          <PillSelector
            extraWrapperClass="Xgroup-[]:hidden"
            pills={pills}
            selectedPill={selectedPill}
            setSelectedPill={setSelectedPill}
          />
          <WidgetFilterSelector
            limitWidth={limitWidth}
            selectedFilterLabel={t(`filter_label_sorting_${sortOrder}`)}
            filters={[
              { label: t("filter_label_sorting_desc"), value: "desc" },
              { label: t("filter_label_sorting_asc"), value: "asc" },
            ]}
            setFilter={(newValue) => {
              console.log(newValue);
              setSortOrder(newValue as "asc" | "desc");
            }}
          />
        </div>
      );
    };
  // , [selectedPill, sortOrder]);

  const DetailsTable: FC<{
    heightLimitClass: string;
    tableColumns: string[];
    useSortableColumns?: boolean;
  }> = ({ heightLimitClass, tableColumns, useSortableColumns = false }) => {
    return (
      <AdminTable
        customWrapperClass={`overflow-auto relative w-full`}
        columns={tableColumns.map((key: string) => {
          return (
            (dataSpecificReport?.report_fields?.find(
              (field) => field.field.id === parseInt(key)
            )?.field?.name || "-") +
            (APP_CONFIG.DEBUG.DISPLAY_VERBOSE ? " (" + key + ")" : "")
          );
        })}
        compact={!useSortableColumns}
        data={
          [...(dataReport?.results || [])]
            // ?.sort((a, b) => sortOrder === 'desc' ? b[graphYAxisDataKey] - a[graphYAxisDataKey] : a[graphYAxisDataKey] - b[graphYAxisDataKey])
            ?.map((resultItem: any) => {
              {
                return {
                  navLink: "",
                  //Object.values(resultItem)
                  cellData: tableColumns.map((column) => {
                    return typeof resultItem[column] === "number" &&
                      !Number.isInteger(resultItem[column])
                      ? resultItem[column].toFixed(2)
                      : resultItem[column];
                  }),
                };
              }
            }) || []
        }
        heightLimitClass={heightLimitClass}
        isLoading={false}
        rightAlignLastColumn={!useSortableColumns}
        useSortableColumns={useSortableColumns}
      />
    );
  };

  return (
    <div className="mt-2">
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="absolute text-[8px] text-field-error-dark -mt-2">
          reportId: {reportId}
        </pre>
      )}

      {/* Filter */}
      <MemoizedWidgetFilterSelector />

      {(!!dataReport && dataReport?.results?.length > 0) || isFetching ? (
        <>
          {isFetching ? (
            <ChartWrapper heightClass="pt-[45%]">
              <img
                src={svgIcons.LoadingAnimation}
                alt="laoding"
                className="absolute size-8 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              />
            </ChartWrapper>
          ) : (
            <>
              <WidgetChartAreaGenerator
                averageReferenceLineValue={
                  dataReport?.results?.[0]?.[averageValueFieldId] || undefined
                }
                barChartMargin={
                  selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                    ? { top: 0, right: 10, bottom: 0, left: -20 }
                    : { top: 0, right: 10, bottom: 20, left: -30 }
                }
                barColor={widgetBarColorLightBlue}
                cellItemsForSectionColor={
                  [EWidgetChartTypes.PIE, EWidgetChartTypes.DOUGHNUT].includes(
                    selectedChartType
                  )
                    ? [...(dataReport?.results || [])]
                    : undefined
                }
                chartType={selectedChartType}
                chartWrapperHeightClass={
                  selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                    ? "pt-[100%]"
                    : "pt-[45%]"
                }
                computedChartData={[...(dataReport?.results || [])]?.sort(
                  (a, b) => b[graphYAxisDataKey] - a[graphYAxisDataKey]
                )
                // ?.sort((a, b) => sortOrder === 'desc' ? b[graphYAxisDataKey] - a[graphYAxisDataKey] : a[graphYAxisDataKey] - b[graphYAxisDataKey])
                }
                graphXAxisDataKey={graphXAxisDataKey}
                graphYAxisDataKey={graphYAxisDataKey}
                legendVerticalAlignClass="justify-start"
                showAverageReferenceLineWithLabel={tOverview(
                  "charts.average_label"
                )}
                t={t}
                widgetType={widgetType}
                xAxisTickLabelWidth={40}
                xAxisTickUsesTranslation={false}
                yAxisTickFormatter={(value) =>
                  value === 0 ? "0" : verticalLabelFormatter(value)
                }
              />
              {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                <pre className="text-[8px] leading-[8px]">
                  graphXAxisDataKey: {graphXAxisDataKey}
                  <br />
                  graphYAxisDataKey: {graphYAxisDataKey}
                  <br />
                  computedChartData:{" "}
                  {JSON.stringify(dataReport?.results, null, 2)}
                </pre>
              )}
              {/* <SwitchChartButton onClick={() => {
                dispatch(setModalsData({ ...modalsState, switchChartTypeForWidget: widgetType, showSwitchChartType: true }))
              }} />
              <ResponsiveContainer className={'absolute top-0 left-0 w-full h-full'}>
                <BarChart
                  width={500}
                  height={500 * 0.45}
                  data={
                    [...dataReport?.results || []]
                    // ?.sort((a, b) => sortOrder === 'desc' ? b[graphYAxisDataKey] - a[graphYAxisDataKey] : a[graphYAxisDataKey] - b[graphYAxisDataKey])
                  }
                  margin={{
                    top: 0,
                    right: 10,
                    bottom: 20,
                    left: -30,
                  }}
                // barCategoryGap={10}
                >
                  <defs>
                    <linearGradient id="colorPositive" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColors.positive.light} stopOpacity={1} />
                      <stop offset="100%" stopColor={chartColors.positive.dark} stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="colorNegative" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor={chartColors.negative.light} stopOpacity={1} />
                      <stop offset="100%" stopColor={chartColors.negative.dark} stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey={graphXAxisDataKey} style={{ fontSize: '8px' }}
                    // tickFormatter={(value) => value.toString().substring(0, 10)}
                    angle={-45}
                    tick={(props) => <ChartsCustomXAxisTick labelWidth={40} {...props} />}
                  />
                  <YAxis
                    dataKey={graphYAxisDataKey} style={{ fontSize: '8px' }}
                    tickFormatter={(value) => value === 0 ? '0' : verticalLabelFormatter(value)}
                  />
                  <Bar
                    dataKey={graphYAxisDataKey}
                    fill={chartColors.bar}
                    radius={[5, 5, 0, 0]}
                  // barSize={25}
                  >
                  </Bar>
                  <ReferenceLine
                    y={
                      dataReport?.results?.reduce((acc, item, _, { length }) => {
                        return acc + item[graphYAxisDataKey] / length;
                      }, 0)
                    }
                    stroke="#229F45"
                    strokeWidth={2}
                    label={{
                      value: tOverview('charts.average_label'),
                      position: 'insideBottomLeft',
                      fontSize: 10,
                      fontWeight: 'bold',
                      fill: '#229F45',
                    }}
                  />
                </BarChart>
              </ResponsiveContainer> */}
            </>
          )}

          {/* Table */}
          {isFetching ? (
            <div
              className={`${tableMinHeightClass} ${tableMaxHeightClass} flex justify-center items-center relative`}
            >
              <img
                src={svgIcons.LoadingAnimation}
                alt="laoding"
                className="absolute size-8 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              />
            </div>
          ) : (
            <>
              <DetailsTable
                heightLimitClass={`${tableMinHeightClass} ${tableMaxHeightClass}`}
                tableColumns={[graphXAxisDataKey, graphYAxisDataKey]}
              />
              <ModalWidgetDetails
                widgetType={widgetType}
                reportId={reportId}
                visible={showDetails}
                onHide={() => setShowDetails(false)}
              >
                <div className="group">
                  <MemoizedWidgetFilterSelector limitWidth={false} />
                </div>
                <DetailsTable
                  heightLimitClass={`${tableMinHeightClass} max-h-[calc(100vh-220px)]`}
                  tableColumns={
                    dataSpecificReport?.report_fields
                      ?.map((field) => field.field.id.toString())
                      ?.filter((field) => field !== averageValueFieldId) || []
                    // !!dataReport?.results?.[0]
                    //   ? [
                    //     ...detailsTablePriorityColumns,
                    //     ...Object.keys(dataReport?.results?.[0])
                    //       .filter((key) => !detailsTablePriorityColumns.includes(key))
                    //   ]
                    //   : []
                  }
                  useSortableColumns={true}
                />
              </ModalWidgetDetails>
            </>
          )}
        </>
      ) : (
        <p className="p-6 text-center text-sm text-heading-gray888">
          {tOverview("no_widget_data")}
        </p>
      )}
      {modalsState.showSwitchChartType &&
        modalsState.switchChartTypeForWidget === widgetType && (
          <ModalSwitchChartType
            selectedChartType={selectedChartType}
            onChartTypeChange={(newChartType) => {
              setSelectedChartType(newChartType);
            }}
          />
        )}
    </div>
  );
};
export default WidgetSafetyEventsAsset;
